import { Invoice, InvoiceSeller } from 'src/models/invoice.model';

export interface SellerDisplay {
  name: string;
  email: string | null;
  phone: string | null;
  teamId: number | null;
}

/**
 * Resolve display fields from an invoice's polymorphic `seller`.
 *
 * Why: The backend returns `seller: { type: 'Team' | 'Vendor', data: { team_owner? } }`
 * but still sends deprecated `team_id` / `homekeeper_data` transitionally. Centralising
 * this avoids sprinkling type checks across every page that renders an invoice.
 */
export function getSellerDisplay(invoice: Invoice | null | undefined): SellerDisplay {
  const empty: SellerDisplay = { name: '', email: null, phone: null, teamId: null };
  if (!invoice) {return empty;}

  const seller: InvoiceSeller | undefined = invoice.seller;

  if (seller?.type === 'Vendor') {
    return {
      name: seller.name || '',
      email: null,
      phone: null,
      teamId: null,
    };
  }

  if (seller?.type === 'Team') {
    const owner = seller.data?.team_owner;
    return {
      name: owner?.name || seller.name || '',
      email: owner?.email || null,
      phone: owner?.phone || null,
      teamId: seller.id,
    };
  }

  // Transitional fallback for responses that haven't been migrated yet.
  return {
    name: invoice.homekeeper_data?.name || '',
    email: invoice.homekeeper_data?.email || null,
    phone: invoice.homekeeper_data?.phone || null,
    teamId: invoice.team_id ?? null,
  };
}

export function isVendorInvoice(invoice: Invoice | null | undefined): boolean {
  return invoice?.seller?.type === 'Vendor';
}

export function isTeamInvoice(invoice: Invoice | null | undefined): boolean {
  return invoice?.seller?.type === 'Team' || (!!invoice?.team_id && !invoice?.seller);
}
